import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import clsx from "clsx";
import { Text, Button, Popin } from "$components";
import useJStyles from "$hooks/useJStyles";
import { useStyles } from "./styles";
import { useAssignCarrier } from "./context/AssignCarrierContext";
import { AssignCarrierFormValues } from "./types";
import { AssignCarrierBasicInfo } from "./AssignCarrierBasicInfo";
import EntrepriseForm from "$containers/Entreprises/EntrepriseForm";
import { CompanyType } from "$types";

type Props = {
  onSubmit: (formValues: AssignCarrierFormValues) => Promise<void>;
  onClose: () => void;
};

export default function AssignCarrierContent({ onSubmit, onClose }: Props) {
  const { handleSubmit, reset } = useFormContext();
  const { jclasses } = useJStyles();
  const classes = useStyles();

  const {
    isOpen,
    error,
    setError,
    defaultValues,
    selectedCompanyTypeToCreate,
    selectedCarrierCompany,
    resetCarrier,
    isCreatingCompany,
    handleCompanySubmit,
    toggleCompanyDrawer,
    isCompanyDrawerOpen,
    companyFormError,
  } = useAssignCarrier();

  const handleClose = useCallback(() => {
    setTimeout(() => {
      reset(defaultValues);
      if (error) {
        resetCarrier();
      }
    }, 500);
    onClose();
  }, [onClose, reset, defaultValues, error, resetCarrier]);

  const handleFormSubmit = useCallback(
    async (formValues: AssignCarrierFormValues) => {
      try {
        await onSubmit(formValues);
        onClose();
        setError(undefined);
      } catch (error) {
        console.error(error);
        setError(error.message);
      }
    },
    [onSubmit, onClose, setError]
  );

  return (
    <>
      <Popin
        openPopin={isOpen}
        toggleDrawer={handleClose}
        containerClassName={classes.popinContainer}
      >
        <div className={clsx(jclasses.jWrapper, classes.wrapper)}>
          <Text type="h2">Affecter un transporteur</Text>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <AssignCarrierBasicInfo />
            {error ? <Text className={classes.errorField}>{error}</Text> : null}
            <div className={classes.buttonsContainer}>
              <Button color="secondary" onClick={handleClose}>
                Retour
              </Button>
              <Button type="submit">Valider</Button>
            </div>
          </form>
        </div>
      </Popin>
      <Popin
        openPopin={isCompanyDrawerOpen}
        toggleDrawer={() => toggleCompanyDrawer(false)}
      >
        <EntrepriseForm
          onSubmit={handleCompanySubmit}
          companyError={companyFormError}
          onReset={() => toggleCompanyDrawer(false)}
          isSubmitting={isCreatingCompany}
          readOnlyFields={["agencyOf", "companyTypes", "contributorOf"]}
          defaultValues={{
            companyTypes:
              selectedCompanyTypeToCreate === "agencyCompanyId"
                ? [CompanyType.AGENCY]
                : [CompanyType.CARRIER],
            ...(selectedCompanyTypeToCreate === "agencyCompanyId" && {
              agencyOf: selectedCarrierCompany,
            }),
          }}
        />
      </Popin>
    </>
  );
}
